import { expVal } from '@confluence/feature-experiments';
import { parse } from 'url';

export const MOBILE_APP_WITH_EDITING_QUERY_PARAM_VALUES = ['mobileAppWithEditing'];
export const MOBILE_APP_WITH_COMMENTING_QUERY_PARAM_VALUES = [
	...MOBILE_APP_WITH_EDITING_QUERY_PARAM_VALUES,
	'mobileAppWithCommenting',
];
export const MOBILE_APP_QUERY_PARAM_VALUES = [
	...MOBILE_APP_WITH_COMMENTING_QUERY_PARAM_VALUES,
	'mobileApp',
];

export const checkForValidParam = (param: string | string[], validParams: string[]): boolean => {
	if (!param || !validParams) {
		return false;
	}

	if (Array.isArray(param)) {
		return param.some((p) => validParams.includes(p));
	}

	return validParams.includes(param);
};

/**
 * This function is for use by the Confluence Mobile App team to detect if the user is in the mobile app.
 * It follows the naming and usage pattern of `isEmebeddedConfluence_DO_NOT_USE`, does not differentiate
 * between different mobile app modes (e.g. `mobileAppWithEditing` vs `mobileAppWithCommenting`), and
 * explicitly avoids using any of our query parameter contexts.
 *
 * The preferred way to detect if the user is in the mobile app is to use the `useIsMobileApp` hook
 * coming from `@confluence/mobile-app-detection`.
 *
 * @param location - The location object to check for the query parameters.
 * @returns `true` if the request is coming from the mobile app, `false` otherwise.
 */
export const isMobileApp_DO_NOT_USE = (location?: { search: string }): boolean => {
	if (!expVal('cc_mobile_query_param', 'isEnabled', false)) {
		return false;
	}

	if (!location) {
		location = window.location;
	}

	const { query } = parse(location.search, true);

	return (
		checkForValidParam(query.presentationMode, MOBILE_APP_QUERY_PARAM_VALUES) ||
		checkForValidParam(query.parentProduct, MOBILE_APP_QUERY_PARAM_VALUES)
	);
};

type WindowWithMobileQueryParamExp = Window & { __CC_MOBILE_QUERY_PARAM_EXP__?: boolean };

// The difference between this and the `isMobileApp_DO_NOT_USE` function is this is for use in
// places where we need to check the experiment-gate before StatSig is initialized on the client.
// We will be removing this function once we remove the experiment-gate.
export const isMobileApp_checkGateViaSSR_DO_NOT_USE = (location?: { search: string }): boolean => {
	if (!(window as WindowWithMobileQueryParamExp).__CC_MOBILE_QUERY_PARAM_EXP__) {
		return false;
	}

	if (!location) {
		location = window.location;
	}

	const { query } = parse(location.search, true);

	return (
		checkForValidParam(query.presentationMode, MOBILE_APP_QUERY_PARAM_VALUES) ||
		checkForValidParam(query.parentProduct, MOBILE_APP_QUERY_PARAM_VALUES)
	);
};
